import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Image, Edit, Trash2, Plus, ArrowLeft, LogOut, Edit3 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminGallery = () => {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [images, setImages] = useState([]);
  const [availableFiles, setAvailableFiles] = useState([]);
  const [showFileList, setShowFileList] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [renameDialog, setRenameDialog] = useState({ open: false, file: null, newName: '' });
  const [formData, setFormData] = useState({
    image_url: '',
    alt_text: '',
    order: 0
  });

  useEffect(() => {
    if (!loading && !user) {
      navigate('/admin/login');
    } else if (user) {
      fetchImages();
      fetchAvailableFiles();
    }
  }, [user, loading, navigate]);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/gallery`, { withCredentials: true });
      setImages(response.data);
    } catch (error) {
      console.error('Error fetching images:', error);
      toast.error('Failed to fetch gallery images');
    }
  };

  const fetchAvailableFiles = async () => {
    try {
      const response = await axios.get(`${API}/admin/gallery-files`, { withCredentials: true });
      setAvailableFiles(response.data.files || []);
    } catch (error) {
      console.error('Error fetching available files:', error);
      toast.error('Failed to fetch available files');
    }
  };

  const handleDeleteImage = async (filename) => {
    if (!confirm(`Are you sure you want to delete ${filename}? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/delete-gallery-image/${filename}`, { withCredentials: true });
      toast.success(`Image ${filename} deleted successfully`);
      await fetchAvailableFiles(); // Refresh the list
      await fetchImages(); // Refresh gallery in case it was in database
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete image');
    }
  };

  const handleRenameImage = async () => {
    if (!renameDialog.file || !renameDialog.newName.trim()) {
      toast.error('Please enter a valid new name');
      return;
    }

    try {
      const response = await axios.put(
        `${API}/admin/rename-gallery-image/${renameDialog.file.filename}?new_name=${encodeURIComponent(renameDialog.newName)}`,
        {},
        { withCredentials: true }
      );

      toast.success(response.data.message);
      setRenameDialog({ open: false, file: null, newName: '' });
      await fetchAvailableFiles(); // Refresh the list
      await fetchImages(); // Refresh gallery in case it was in database
    } catch (error) {
      console.error('Error renaming image:', error);
      toast.error(error.response?.data?.detail || 'Failed to rename image');
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const openDialog = (image = null) => {
    if (image) {
      setEditingImage(image);
      setFormData({
        image_url: image.image_url,
        alt_text: image.alt_text,
        order: image.order
      });
    } else {
      setEditingImage(null);
      setFormData({
        image_url: '',
        alt_text: '',
        order: images.length
      });
    }
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingImage(null);
    setFormData({
      image_url: '',
      alt_text: '',
      order: 0
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData
      };
      
      if (editingImage) {
        await axios.put(
          `${API}/gallery/${editingImage.id}`,
          dataToSend,
          { withCredentials: true }
        );
        toast.success('Gallery image updated successfully');
      } else {
        await axios.post(
          `${API}/gallery`,
          dataToSend,
          { withCredentials: true }
        );
        toast.success('Gallery image added successfully');
      }
      closeDialog();
      fetchImages();
    } catch (error) {
      console.error('Error saving gallery image:', error);
      toast.error('Failed to save gallery image');
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }
    try {
      await axios.delete(`${API}/gallery/${imageId}`, { withCredentials: true });
      toast.success('Gallery image deleted successfully');
      fetchImages();
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Failed to delete gallery image');
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="admin-container">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-content">
          <div className="admin-logo-section" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_lethbridge-angels/artifacts/znfka10j_RA-blacktransparent%20cropped%20no%20vol.png" 
              alt="Rebel Angels" 
              className="admin-brand-logo"
            />
          </div>
          <div className="admin-user-section">
            <img src={user.picture} alt={user.name} className="admin-user-avatar" />
            <span className="admin-user-name">{user.name}</span>
            <Button variant="outline" className="admin-logout-button" onClick={handleLogout}>
              <LogOut className="button-icon" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-content">
          <div className="admin-page-header">
            <Button className="admin-add-button" onClick={() => navigate('/admin/dashboard')}>
              <ArrowLeft className="button-icon" />
              Back to Dashboard
            </Button>
          </div>

          <div className="admin-title-row">
            <h1 className="admin-page-title">Manage Gallery</h1>
            <Button className="admin-add-button" onClick={() => openDialog()}>
              <Plus className="button-icon" />
              Add Image
            </Button>
          </div>

          {/* Available Files Browser */}
          <div className="admin-section" style={{ marginBottom: '2rem' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem', 
              marginBottom: '1rem',
              cursor: 'pointer' 
            }} onClick={() => setShowFileList(!showFileList)}>
              <h3 style={{ 
                color: 'var(--retro-cream)', 
                fontSize: '1.1rem', 
                margin: 0 
              }}>
                📁 Available Gallery Images ({availableFiles.length})
              </h3>
              <span style={{ color: 'var(--retro-cream)', fontSize: '0.9rem' }}>
                {showFileList ? '▼' : '▶'} Click to {showFileList ? 'hide' : 'show'}
              </span>
            </div>
            
            {showFileList && (
              <div style={{ 
                background: 'rgba(220, 20, 60, 0.1)', 
                border: '1px solid var(--retro-red)', 
                borderRadius: '8px', 
                padding: '1rem',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                {availableFiles.length === 0 ? (
                  <p style={{ color: 'var(--retro-cream)', fontStyle: 'italic' }}>
                    No images found in gallery directory
                  </p>
                ) : (
                  <div style={{ display: 'grid', gap: '0.5rem' }}>
                    {availableFiles.map((file, index) => (
                      <div key={index} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '1rem',
                        background: 'rgba(0,0,0,0.3)',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        border: '2px solid rgba(220, 20, 60, 0.2)',
                        marginBottom: '0.5rem'
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            color: 'var(--retro-cream)', 
                            fontWeight: 'bold',
                            marginBottom: '0.25rem'
                          }}>
                            {file.filename}
                          </div>
                          <div style={{ 
                            color: '#aaa',
                            fontSize: '0.8rem'
                          }}>
                            {file.url} • {(file.size / 1024).toFixed(1)} KB
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Button
                            size="sm"
                            data-testid={`use-file-${index}`}
                            onClick={() => {
                              setFormData({ ...formData, image_url: file.url });
                              setIsDialogOpen(true);
                              toast.success('Image URL added to form - fill in the details and save');
                            }}
                            style={{ 
                              background: 'var(--retro-red)', 
                              color: 'white',
                              padding: '0.5rem 0.75rem',
                              fontSize: '0.8rem'
                            }}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add to Gallery
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setRenameDialog({ open: true, file, newName: file.filename.replace(/\.[^/.]+$/, '') })}
                            style={{ 
                              background: '#4a5568', 
                              color: 'white',
                              padding: '0.5rem 0.75rem',
                              fontSize: '0.8rem'
                            }}
                            title="Rename this file"
                          >
                            <Edit3 className="w-4 h-4 mr-1" />
                            Rename
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleDeleteImage(file.filename)}
                            style={{ 
                              background: '#dc2626', 
                              color: 'white',
                              padding: '0.5rem 0.75rem',
                              fontSize: '0.8rem'
                            }}
                            title="Delete this file permanently"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <p style={{ 
                  color: 'var(--retro-cream)', 
                  fontSize: '0.8rem', 
                  marginTop: '1rem', 
                  fontStyle: 'italic',
                  opacity: 0.8 
                }}>
                  💡 Click "Add to Gallery" to add an image to your gallery database
                </p>
              </div>
            )}
          </div>

          <div className="admin-gallery-grid">
            {images.length === 0 ? (
              <p className="admin-empty-message">No gallery images yet. Add your first image!</p>
            ) : (
              images.map((image) => (
                <Card key={image.id} className="admin-gallery-card">
                  <CardContent className="admin-gallery-card-content">
                    <div className="admin-gallery-image-preview">
                      <img src={image.image_url.startsWith('/api') ? `${BACKEND_URL}${image.image_url}` : image.image_url} alt={image.alt_text || 'Gallery image'} />
                    </div>
                    <div className="admin-gallery-info">
                      <p className="admin-gallery-alt">{image.alt_text || 'No description'}</p>
                      <p className="admin-gallery-order">Order: {image.order}</p>
                    </div>
                    <div className="admin-gallery-actions">
                      <Button variant="outline" size="sm" onClick={() => openDialog(image)}>
                        <Edit className="button-icon-sm" />
                        Edit
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => handleDelete(image.id)}>
                        <Trash2 className="button-icon-sm" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </main>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="admin-dialog">
          <DialogHeader>
            <DialogTitle>{editingImage ? 'Edit Gallery Image' : 'Add New Gallery Image'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="admin-form">
            <div className="admin-form-group">
              <label className="admin-form-label">Image URL</label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="/api/images/gallery/filename.jpg"
              />
              <p style={{ fontSize: '0.85rem', color: 'var(--retro-cream)', opacity: 0.8, marginTop: '0.5rem' }}>
                💡 Select an image from the file list above, or enter a path manually. New images must be added to the codebase before deployment.
              </p>
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Alt Text / Description</label>
              <Input
                value={formData.alt_text}
                onChange={(e) => setFormData({ ...formData, alt_text: e.target.value })}
                placeholder="Description of the image"
              />
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Display Order</label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                placeholder="0"
              />
            </div>
            {formData.image_url && (
              <div className="admin-form-group">
                <label className="admin-form-label">Preview</label>
                <div className="admin-image-preview">
                  <img src={formData.image_url.startsWith('/api') ? `${BACKEND_URL}${formData.image_url}` : formData.image_url} alt="Preview" onError={(e) => e.target.src = ''} />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
              <Button type="submit">
                {editingImage ? 'Update' : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={renameDialog.open} onOpenChange={(open) => setRenameDialog({ ...renameDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Image</DialogTitle>
          </DialogHeader>
          <div className="admin-form-group">
            <label className="admin-form-label">Current Name</label>
            <Input value={renameDialog.file?.filename || ''} disabled />
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">New Name (without extension)</label>
            <Input
              value={renameDialog.newName}
              onChange={(e) => setRenameDialog({ ...renameDialog, newName: e.target.value })}
              placeholder="new-image-name"
              data-testid="rename-input"
            />
            <p style={{ fontSize: '0.85rem', color: '#aaa', marginTop: '0.5rem' }}>
              ⚠️ Filenames are automatically converted to lowercase for compatibility
            </p>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setRenameDialog({ open: false, file: null, newName: '' })}
            >
              Cancel
            </Button>
            <Button onClick={handleRenameImage}>
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGallery;
